import { Box, Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Main card wrapper with border and padding
 */
export const StyledCard = styled(Stack)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.common.white,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: theme.customShape.borderRadiusXLarge,
  padding: theme.spacing(6),
  gap: theme.spacing(3.5),
  boxShadow: theme.shadows[4],
  width: '100%',
  height: '100%',
}));

/**
 * Header container - flexbox for label and pin button
 */
export const StyledPinHeader = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}));

/**
 * Pin label text styling
 */
export const StyledPinLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.43,
  color: theme.palette.text.third,
}));

/**
 * Pin button styling
 */
interface PinButtonProps {
  isPinned?: boolean;
}

export const StyledPinButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isPinned',
})<PinButtonProps>(({ theme }) => ({
  minWidth: 'unset',
  width: theme.spacing(8),
  height: theme.spacing(8),
  padding: 0,
  borderRadius: theme.customShape.borderRadiusFull,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },

  '& .MuiSvgIcon-root': {
    fontSize: theme.typography.pxToRem(24),
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short,
    }),
  },
}));

/**
 * Symbol section container
 */
export const StyledSymbolContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

/**
 * Currency circle with abbreviation
 */
export const StyledCurrencyCircle = styled(Stack)(({ theme }) => ({
  width: theme.spacing(14.5), // 58px
  height: theme.spacing(14.5), // 58px
  borderRadius: '50%',
  background: theme.palette.background.blue,
  alignItems: 'center',
  justifyContent: 'center',
}));

/**
 * Currency abbreviation text in circle
 */
export const StyledCurrencyAbbreviation = styled(Typography)(({ theme }) => ({
  fontSize: '21.55px !important',
  fontWeight: 500,
  lineHeight: 1.4,
  letterSpacing: '0.1078px',
  color: theme.palette.info.main,
}));

/**
 * Symbol heading with name and category tag
 */
export const StyledSymbolHeading = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
}));

/**
 * Symbol name text
 */
export const StyledSymbolName = styled(Typography)(({ theme }) => ({
  fontSize: '24px !important',
  fontWeight: 500,
  lineHeight: 1,
  letterSpacing: '0.036px',
  color: theme.palette.text.primary,
}));

/**
 * Category tag
 */
export const StyledCategoryTag = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  height: theme.spacing(6),
  padding: theme.spacing(0, 1),
  borderRadius: theme.customShape.borderRadiusSmall,
  border: `1px solid #4B84FE`,
  backgroundColor: `#4B84FE1A`, // 10% opacity
}));

/**
 * Category tag text
 */
export const StyledCategoryTagText = styled(Typography)(({ theme }) => ({
  fontSize: '12px !important',
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color: theme.palette.text.primary,
}));

/**
 * Symbol description text
 */
export const StyledSymbolDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

/**
 * Price section container
 */
export const StyledPriceContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

/**
 * Current price text
 */
export const StyledCurrentPrice = styled(Typography)(({ theme }) => ({
  fontSize: '36px !important',
  fontWeight: 700,
  lineHeight: 1,
  letterSpacing: '0.09px',
  color: theme.palette.text.primary,
}));

/**
 * Price change container with tag and percentage
 */
export const StyledPriceChangeContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

/**
 * Price change tag (colored based on direction)
 */
interface PriceChangeTagProps {
  changeColor: 'success' | 'error';
}

export const StyledPriceChangeTag = styled(Stack)<PriceChangeTagProps>(
  ({ theme, changeColor }) => ({
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.spacing(6), // 24px
    padding: theme.spacing(0, 1),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${
      changeColor === 'success'
        ? theme.palette.success.accent
        : theme.palette.error.main
    }`,
    backgroundColor:
      changeColor === 'success'
        ? `${theme.palette.success.accent}1A` // 10% opacity
        : `${theme.palette.error.main}1A`, // 10% opacity
  }),
);

/**
 * Price change tag text
 */
export const StyledPriceChangeText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.primary,
}));

/**
 * Price change percentage text
 */
export const StyledPriceChangePercent = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.primary,
}));

/**
 * Sentiment section container
 */
export const StyledSentimentContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

/**
 * Sentiment section label
 */
export const StyledSentimentLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.secondary,
}));

/**
 * Tooltips row (left and right tooltips) - positioned above progress bar
 */
export const StyledTooltipsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: theme.spacing(1.875), // 15px gap to progress bar
}));

/**
 * Individual tooltip bubble (always visible)
 */
export const StyledTooltipBubble = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.text.secondary}`,
  borderRadius: theme.spacing(2.5),
  boxShadow: theme.shadows[2],
  alignItems: 'center',
  gap: theme.spacing(1),
  position: 'relative',
  maxWidth: '340px',
  padding: `0 ${theme.spacing(1.5)}`,

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-6px',
    left: '50%',
    transform: 'translateX(-50%) rotate(45deg)',
    width: '10px',
    height: '10px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.text.secondary}`,
    borderTop: 'none',
    borderLeft: 'none',
  },
}));

export const StyledBallLeft = styled(Box)(({ theme }) => ({
  width: theme.spacing(4), // 16px
  height: theme.spacing(4), // 16px
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
}));

export const StyledBallRight = styled(Box)(({ theme }) => ({
  width: theme.spacing(4), // 16px
  height: theme.spacing(4), // 16px
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
}));

/**
 * Tooltip label text inside bubble
 */
export const StyledTooltipLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  height: theme.spacing(8.25), // 33px
  width: theme.spacing(29), // 116px
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

/**
 * Sentiment progress bar container
 */
export const StyledSentimentProgressBar = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  width: '100%',
  height: theme.spacing(1), // 4px
  borderRadius: theme.spacing(0.5),
  overflow: 'hidden',
}));

/**
 * Asset sentiment bar (left side, purple)
 */
interface SentimentBarPartProps {
  width: number;
}

export const StyledAssetSentimentBar = styled(Box)<SentimentBarPartProps>(
  ({ theme, width }) => ({
    width: `${width}%`,
    height: '100%',
    background: theme.palette.primary.main,
  }),
);

/**
 * Market sentiment bar (right side, orange)
 */
export const StyledMarketSentimentBar = styled(Box)<SentimentBarPartProps>(
  ({ theme, width }) => ({
    width: `${width}%`,
    height: '100%',
    background: theme.palette.warning.main,
  }),
);

/**
 * Market data grid container
 */
export const StyledMarketDataGrid = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
}));

/**
 * Individual market data row
 */
export const StyledMarketDataRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
}));

/**
 * Market data label (left side)
 */
export const StyledMarketDataLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.third,
}));

/**
 * Market data value (right side)
 */
export const StyledMarketDataValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.main,
}));

/**
 * Bottom tags container
 */
export const StyledBottomTagsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

/**
 * Tag component (for volume and position)
 */
interface TagProps {
  borderColor: string;
  backgroundColor: string;
}

export const StyledTag = styled(Stack)<TagProps>(
  ({ theme, borderColor, backgroundColor }) => ({
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.spacing(6), // 24px
    padding: theme.spacing(0, 1),
    borderRadius: theme.customShape.borderRadiusSmall,
    border: `1px solid ${borderColor}`,
    backgroundColor,
  }),
);

/**
 * Tag text
 */
export const StyledTagText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.06px',
  color: theme.palette.text.primary,
}));

/**
 * Loading state container
 */
export const StyledLoadingContainer = styled(Stack)(({ theme }) => ({
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: theme.spacing(100),
}));
